import { router } from "@inertiajs/react";
import PeopleCircles from "@/components/PeopleCircles";
import { people_task_path } from "@/lib/routes";
import type { Person, Task } from "@/pages/Tasks/task";

const LABEL = "text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground";

// One column: label over the shared people control (PeopleCircles). The
// server keeps at least one person on the list.
function Column({ label, members, people, canEdit, onChange, emptyLabel }: {
  label: string;
  members: Person[];
  people: Person[];
  canEdit: boolean;
  onChange: (ids: string[]) => void;
  // Warning pill when the list is empty; omit to show only the "+" (reviewers
  // are optional on some task kinds, so an empty list is not a problem).
  emptyLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className={LABEL}>{label}</p>
      <PeopleCircles
        title={label}
        people={people}
        selectedIds={members.map((m) => m.id)}
        onChange={onChange}
        editable={canEdit}
        emptyLabel={emptyLabel}
        selectedHeading="On this task"
      />
    </div>
  );
}

// Trello-style members strip under the due line: the people in charge and
// the reviewers as initials circles (name on hover). For now anyone who can
// see the task may change either list (Workflow::People) — this task only.
export default function TaskPeople({ task, people }: { task: Task; people: Person[] }) {
  const change = (role: "pics" | "reviewers") => (ids: string[]) =>
    router.patch(people_task_path(task.id), { role, user_ids: ids }, { preserveScroll: true });

  return (
    <div className="mt-3 flex gap-7">
      <Column
        label="In charge"
        members={task.responsible?.users ?? []}
        people={people}
        canEdit={task.can.edit_pics}
        onChange={change("pics")}
        emptyLabel="No one assigned"
      />
      <Column
        label={task.reviewers.length > 1 ? "Reviewers" : "Reviewer"}
        members={task.reviewers}
        people={people}
        canEdit={task.can.edit_reviewers}
        onChange={change("reviewers")}
      />
    </div>
  );
}
